import { Component, computed, input, signal, ViewEncapsulation } from '@angular/core';
import { IncentiveNamePipe } from './ft-finalist-incentives.component';

/**
 * Native port of the AngularJS `ftFinalistGoalSelector` directive — the incentive dropdown
 * inside the `finalistsGoalsModal` (`.ft-dropdown.goal-incentives` over the active property
 * incentives). Toggles the `open` class on click (close on document click); `setIncentive`
 * mutates `incentive.incentiveUUID` in place (the parent holds the same object reference).
 */
@Component({
    selector: 'ft-finalist-goal-selector-ng',
    imports: [IncentiveNamePipe],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-finalist-goal-selector.component.html',
    host: {
        '(document:click)': 'onDocumentClick()',
    }
})
export class FtFinalistGoalSelectorComponent {
    /** The finalist incentive slot ({ incentiveUUID }); mutated in place on select. */
    readonly incentive = input<any>();
    /** The property incentives the dropdown lists (already tag-filtered by the host). */
    readonly propertyIncentives = input<any[]>([]);

    protected readonly open = signal(false);

    /** v1 `propertyIncentives | filter:{isActive: true}` (default comparator = substring on String()). */
    protected readonly activeIncentives = computed<any[]>(() =>
        (this.propertyIncentives() || []).filter(
            (pi) => pi && String(pi.isActive).toLowerCase().indexOf('true') !== -1,
        )
    );

    /** v1 `getPropertyIncentive` — resolve the selected incentive object by `incentiveUUID`. */
    protected getPropertyIncentive(incentive: any): any {
        if (incentive == null || incentive.incentiveUUID === undefined) return '';
        return (this.propertyIncentives() || []).find((pi) => pi.id === incentive.incentiveUUID);
    }

    /** v1 `setIncentive` — write the picked incentive's id onto the slot (in-place). */
    protected setIncentive(propertyIncentive: any): void {
        const incentive = this.incentive();
        if (incentive) incentive.incentiveUUID = propertyIncentive.id;
    }

    /** v1 `.goal-incentives` click → toggleClass('open') + stopPropagation. */
    protected toggle(event: Event): void {
        event.stopPropagation();
        this.open.update((open) => !open);
    }

    /** v1 `$document.bind('click', removeClass('open'))`. */
    protected onDocumentClick(): void {
        this.open.set(false);
    }
}

/**
 * Native port of the AngularJS `ftRunnerups` directive — the `@totalRunnerUps` count dropdown
 * (1–20) in the runner-ups panel. Same `open`-class toggle + document-click close; picks mutate
 * `incentive['@totalRunnerUps']` in place.
 */
@Component({
    selector: 'ft-runnerups-ng',
    imports: [],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-runnerups.component.html',
    host: {
        '(document:click)': 'onDocumentClick()',
    }
})
export class FtRunnerupsComponent {
    /** The runner-ups incentive slot carrying `@totalRunnerUps`. */
    readonly incentive = input<any>();

    /** v1 `_.range(1, 21)` → [1..20]. */
    protected readonly numbers = Array.from({ length: 20 }, (_v, i) => i + 1);

    protected readonly open = signal(false);

    protected setCount(n: number): void {
        const incentive = this.incentive();
        if (incentive) incentive['@totalRunnerUps'] = n;
    }

    protected toggle(event: Event): void {
        event.stopPropagation();
        this.open.update((open) => !open);
    }

    protected onDocumentClick(): void {
        this.open.set(false);
    }
}
